import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Lock, LogOut, KeyRound } from "lucide-react";

/**
 * Full-screen Sperr-Overlay über der Kasse.
 * - Erfordert die Kassierer-PIN zum Entsperren
 * - 3 Fehlversuche → automatischer Logout
 * - ESC macht NICHTS (bewusst — Overlay muss aktiv entsperrt werden)
 *
 * Props:
 *   open: boolean
 *   cashierName: string
 *   onUnlock: () => void
 *   onLogout: () => void   (z. B. für Kassierer-Wechsel)
 */
export default function POSLockOverlay({ open, cashierName, onUnlock, onLogout }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [hasPin, setHasPin] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPin(""); setAttempts(0);
    api.get("/users/me/pin-status").then(({ data }) => setHasPin(!!data.has_pin)).catch(() => setHasPin(false));
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  const verify = async (val) => {
    if (!val || val.length < 4) return;
    setBusy(true);
    try {
      await api.post("/pos/pin-verify", { pin: val });
      toast.success("Kasse entsperrt");
      onUnlock?.();
    } catch (e) {
      const status = e.response?.status;
      const next = attempts + 1;
      setAttempts(next);
      setPin("");
      if (status === 401 && next >= 3) {
        toast.error("3× falsche PIN — Kassierer wird abgemeldet");
        setTimeout(() => onLogout?.(), 800);
        return;
      }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "PIN falsch");
      setTimeout(() => inputRef.current?.focus(), 50);
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4" data-testid="pos-lock-overlay">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 mb-4">
            <Lock size={32} className="text-[#D4AF37]" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.5em] text-[#D4AF37]">Kasse gesperrt</div>
          <h2 className="font-heading text-4xl mt-2 text-[#F5F0E6]">THE MOVIE HOUSE</h2>
          <div className="text-sm text-[#A8A398] mt-2">
            {cashierName ? <>Kassierer: <span className="text-[#EEDC82]">{cashierName}</span></> : "PIN eingeben zum Entsperren"}
          </div>
        </div>

        {!hasPin ? (
          <div className="card-lux p-6 text-center" data-testid="pos-lock-no-pin">
            <KeyRound size={24} className="text-[#EE9CA1] mx-auto mb-2" />
            <div className="text-sm text-[#EE9CA1] mb-2">Keine Kassierer-PIN gesetzt</div>
            <div className="text-xs text-[#A8A398] mb-4">
              Bitte in <span className="text-[#D4AF37]">Einstellungen → Kassierer-PIN</span> eine PIN hinterlegen. Alternativ jetzt abmelden.
            </div>
            <button onClick={onLogout} data-testid="pos-lock-logout-nopin"
              className="w-full border border-[#7A1921] text-[#EE9CA1] py-3 rounded uppercase tracking-widest text-[11px] hover:bg-[#7A1921]/20 inline-flex items-center justify-center gap-2">
              <LogOut size={14} /> Abmelden
            </button>
          </div>
        ) : (
          <div className="card-lux p-6" data-testid="pos-lock-pin-form">
            <label className="text-[10px] uppercase tracking-[0.35em] text-[#A8A398] block text-center mb-3">Kassierer-PIN</label>
            <input ref={inputRef} type="password" inputMode="numeric" pattern="[0-9]*" maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") verify(pin); }}
              data-testid="pos-lock-pin-input"
              className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-4 py-4 font-mono text-3xl text-center tracking-[0.6em] focus:border-[#D4AF37] outline-none" />
            {attempts > 0 && (
              <div className="text-[10px] text-[#EE9CA1] mt-2 text-center uppercase tracking-widest">
                {attempts}/3 Fehlversuche
              </div>
            )}
            {/* Ziffernblock (Touch-Screen tauglich) */}
            <div className="grid grid-cols-3 gap-2 mt-4" data-testid="pos-lock-keypad">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button key={n} onClick={() => setPin((p) => (p + String(n)).slice(0, 8))}
                  data-testid={`pos-lock-key-${n}`}
                  className="py-4 border border-[#2A2A2A] hover:border-[#D4AF37] rounded text-2xl font-mono text-[#EEDC82]">
                  {n}
                </button>
              ))}
              <button onClick={() => setPin("")}
                className="py-4 border border-[#2A2A2A] hover:border-[#EE9CA1] rounded text-xs uppercase tracking-widest text-[#EE9CA1]">
                C
              </button>
              <button onClick={() => setPin((p) => (p + "0").slice(0, 8))}
                data-testid="pos-lock-key-0"
                className="py-4 border border-[#2A2A2A] hover:border-[#D4AF37] rounded text-2xl font-mono text-[#EEDC82]">
                0
              </button>
              <button onClick={() => setPin((p) => p.slice(0, -1))}
                data-testid="pos-lock-backspace"
                className="py-4 border border-[#2A2A2A] hover:border-[#D4AF37] rounded text-xs uppercase tracking-widest text-[#A8A398]">
                ⌫
              </button>
            </div>
            <button onClick={() => verify(pin)} disabled={busy || pin.length < 4} data-testid="pos-lock-unlock"
              className="w-full mt-4 bg-[#D4AF37] hover:bg-[#EEDC82] text-black py-3 rounded uppercase tracking-widest text-xs disabled:opacity-40 inline-flex items-center justify-center gap-2">
              <Lock size={14} /> {busy ? "Prüfe …" : "Entsperren"}
            </button>
            <button onClick={onLogout} data-testid="pos-lock-logout"
              className="w-full mt-2 border border-[#2A2A2A] text-[#A8A398] py-2 rounded uppercase tracking-widest text-[10px] hover:border-[#EE9CA1] hover:text-[#EE9CA1] inline-flex items-center justify-center gap-2">
              <LogOut size={12} /> Kassierer wechseln (Abmelden)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
